import { AngularLayout, FlexLayout } from '@rhtml/modifiers';
import { Component, css, html, LitElement } from '@rxdi/lit-html';
import { ReactivumTheme } from '@shared/styles/theme';

/**
 * @customElement problem-component
 */
@Component({
  selector: 'problem-component',
  styles: [
    css`
      ${ReactivumTheme}

      :host {
        display: block;
        color: var(--text-primary);
      }

      .story-content p {
        margin-bottom: 16px;
      }

      .story-content p:last-child {
        margin-bottom: 0;
      }
    `,
  ],
  modifiers: [...FlexLayout, ...AngularLayout],
  template() {
    return html`
      <section class="section" id="about">
        <div class="container">
          <h2 class="section-title">The Problem We Solved</h2>
          <div class="card card-bordered">
            <h3 class="card-title">The State of Web Development in 2019</h3>
            <div class="card-description">
              <p>
                When we started Reactive Solutions in 2019, the web development landscape was in disarray. Major
                frameworks like Angular 2 were introducing small version upgrades and minor features for years, while
                ignoring valuable community contributions. At that time, there were over 3,000 open pull requests in the
                Angular repository—many containing really nice features that never made it to production. The marketing
                strategy was to provide small changes, bump the version, then write 1,000 blog posts about it with
                clickbait titles and ads.
              </p>
              <p>
                Many of these tickets were bugs or library bumps to use newer versions of Node.js. Others were packages
                built with different versions of Angular that were compatible to a point, but if the maintainer didn't
                push new versions, it would stop you from upgrading or force you to postpone updates due to the multiple
                changes required.
              </p>
              <p>
                The backend world wasn't much better. It was missing types, and many developers resisted adopting
                technologies like TypeScript, causing code quality to suffer significantly. Many libraries had
                vulnerabilities, and some would break completely when introducing patch versions. Literally nobody was
                familiar with semantic versioning—what patch, major, and minor versions meant, and when to introduce
                them. Frameworks weren't abstract enough, and this is where the interesting part began.
              </p>
            </div>
          </div>

          <div class="card card-bordered">
            <h3 class="card-title">The Bold Decision</h3>
            <div class="card-description">
              <p>
                We at Reactive Solutions decided to make a bold move and create our own libraries and frameworks to
                provide better Developer Experience and easier maintainability of the systems we were building. This is
                where the fun began—we started with the basics using pure Node.js and decided to use Dependency
                Injection as the main pattern with TypeScript annotations (decorators) and create abstractions around
                them based on our understanding of Angular 2.
              </p>
              <p>
                Our main goal was to make it less coupled, still easy to maintain, and without introducing breaking
                changes—it was more like a mindset. We also wanted the injection to be async, but later we realized it
                was almost impossible due to the Node.js event loop and how it handles async jobs. This realization led
                us to create the @rhtml, @rxdi, and @gapi npm libraries.
              </p>
            </div>
          </div>
        </div>
      </section>
    `;
  },
})
export class ProblemComponent extends LitElement {}
